import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";

/**
 * Single source of truth for module-level permission checks on the frontend.
 * Mirrors the backend's checkPermission middleware exactly: TENANT_ADMIN/SUPER_ADMIN
 * bypass everything, everyone else is checked against the effective permissions
 * returned by GET /tenant-users/my-permissions (position-first, role-fallback).
 *
 * Use this instead of re-deriving isAdmin/canSeeModule inline in every page —
 * that duplication is exactly how the Electricity Bills gap happened.
 */
export function usePermissions() {
  const { user, permissions } = useContext(AuthContext);
  const effectiveRole = user?.effectiveRole || user?.role;
  const isAdmin = effectiveRole === "TENANT_ADMIN" || effectiveRole === "SUPER_ADMIN";

  const find = (module: string) => permissions.find((p) => p.module === module);

  const canRead = (module: string): boolean => isAdmin || !!find(module)?.canRead;
  const canWrite = (module: string): boolean => isAdmin || !!find(module)?.canWrite;
  const canDelete = (module: string): boolean => isAdmin || !!find(module)?.canDelete;

  return { isAdmin, canRead, canWrite, canDelete };
}
